import { ServicePaymentMethod, ServiceReserveStatus } from "@/apis/clients";
import {
  EventSeminarServiceInfo,
  FormPayment,
  ServiceFormServiceInputs,
  ServiceInfo,
} from "@/states/service-form/types";

export type CouponTargetPayload = {
  service_detail_ids?: number[];
  service_details?: { id: number; quantity: number }[];
  service_menu_id?: number;
};

export type CouponOptionPayload = {
  service_option_ids?: number[];
  service_menu_option_ids?: number[];
};

const resolveSelectedDetailIds = (
  service: EventSeminarServiceInfo | ServiceInfo,
  serviceInputs: ServiceFormServiceInputs,
): number[] => {
  if (
    service.type === "event-seminar" &&
    service.reserveStatus === ServiceReserveStatus.ALL
  ) {
    return service.details.map((detail) => detail.id);
  }

  if (service.type === "event-seminar" || service.type === "form") {
    return serviceInputs.detailIds;
  }

  return [];
};

export const calculateBaseBillingAmount = (
  service: ServiceInfo | undefined,
  serviceInputs: ServiceFormServiceInputs | undefined,
): number => {
  if (!service || !serviceInputs) {
    return 0;
  }

  let billingAmount = 0;

  if (service.type === "calendar") {
    const serviceMenuOptionIds = serviceInputs.serviceMenuOptionIds ?? [];
    const selectedMenu = service.menus.find(
      (menu) => menu.id === serviceInputs.serviceMenuId,
    );
    if (!selectedMenu) {
      return 0;
    }

    billingAmount += selectedMenu.price ?? 0;
    selectedMenu.options.forEach((option) => {
      if (serviceMenuOptionIds.includes(option.id)) {
        billingAmount += option.amount ?? option.price ?? 0;
      }
    });

    return billingAmount;
  }

  if (
    service.type === "event-seminar" &&
    service.reserveStatus === ServiceReserveStatus.ALL
  ) {
    const firstDetail = service.details[0];
    if (firstDetail?.price) {
      const quantity = serviceInputs.quantity?.[firstDetail.id] ?? 1;
      billingAmount += firstDetail.price * quantity;
    }
  } else if (service.type === "event-seminar") {
    const selectedDetailIds = resolveSelectedDetailIds(service, serviceInputs);
    service.details.forEach((detail) => {
      if (selectedDetailIds.includes(detail.id) && detail.price) {
        billingAmount +=
          detail.price * (serviceInputs.quantity?.[detail.id] ?? 1);
      }
    });
  } else if (service.type === "form") {
    const selectedDetailIds = resolveSelectedDetailIds(service, serviceInputs);
    service.details.forEach((detail) => {
      if (selectedDetailIds.includes(detail.id) && detail.price) {
        billingAmount += detail.price;
      }
    });
  } else if (
    service.type === "content-webinar" &&
    service.paid_flag !== undefined
  ) {
    service.details?.forEach((detail) => {
      billingAmount += detail.price ?? 0;
    });
  }

  service.options?.forEach((option) => {
    if (serviceInputs.optionIds.includes(option.id)) {
      billingAmount += option.price ?? option.amount ?? 0;
    }
  });

  return billingAmount;
};

export const buildCouponTargetPayload = (
  service: ServiceInfo | undefined,
  serviceInputs: ServiceFormServiceInputs | undefined,
): CouponTargetPayload | null => {
  if (!service || !serviceInputs) {
    return null;
  }

  if (service.type === "calendar") {
    if (!serviceInputs.serviceMenuId) {
      return null;
    }

    return {
      service_menu_id: serviceInputs.serviceMenuId,
    };
  }

  if (service.type !== "event-seminar" && service.type !== "form") {
    return null;
  }

  const serviceDetailIds = resolveSelectedDetailIds(service, serviceInputs);
  if (serviceDetailIds.length === 0) {
    return null;
  }

  if (service.type === "event-seminar") {
    const serviceDetails = serviceDetailIds.map((id) => ({
      id,
      quantity: serviceInputs.quantity?.[id] ?? 1,
    }));

    return {
      service_detail_ids: serviceDetailIds,
      service_details: serviceDetails,
    };
  }

  return {
    service_detail_ids: serviceDetailIds,
  };
};

export const buildCouponOptionPayload = (
  service: ServiceInfo | undefined,
  serviceInputs: ServiceFormServiceInputs | undefined,
): CouponOptionPayload => {
  if (!service || !serviceInputs) {
    return {};
  }

  if (service.type === "calendar") {
    const selectedMenuOptionIds = serviceInputs.serviceMenuOptionIds ?? [];
    const serviceMenuOptionIds =
      selectedMenuOptionIds.length > 0
        ? selectedMenuOptionIds
        : serviceInputs.optionIds ?? [];

    return serviceMenuOptionIds.length > 0
      ? {
          service_menu_option_ids: serviceMenuOptionIds,
        }
      : {};
  }

  if (service.type === "event-seminar" || service.type === "form") {
    const optionIds = serviceInputs.optionIds ?? [];

    return optionIds.length > 0
      ? {
          service_option_ids: optionIds,
        }
      : {};
  }

  return {};
};

export const buildCouponContextKey = (
  service: ServiceInfo | undefined,
  serviceInputs: ServiceFormServiceInputs | undefined,
): string => {
  const targetPayload = buildCouponTargetPayload(service, serviceInputs);

  return JSON.stringify({
    serviceType: service?.type,
    detailIds: targetPayload?.service_detail_ids ?? [],
    details:
      targetPayload?.service_details?.map((detail) => ({
        id: detail.id,
        quantity: detail.quantity,
      })) ?? [],
    serviceMenuId: targetPayload?.service_menu_id ?? null,
  });
};

export const getCreditSettlementAmount = (amount: number): number => {
  if (amount === 0) {
    return 0;
  }

  return amount < 300 ? 300 : amount;
};

export const calculateSettlementAmount = (
  amount: number,
  settlementMethod?: number | null,
): number => {
  if (settlementMethod === ServicePaymentMethod.CREDIT) {
    return getCreditSettlementAmount(amount);
  }

  return amount;
};

export const hasAppliedCouponForContext = (
  payment: FormPayment | undefined,
  currentContextKey: string,
): boolean =>
  !!payment?.couponCode &&
  !!payment?.couponId &&
  payment.couponContextKey === currentContextKey;

export type PricingSummary = {
  baseAmount: number;
  discountAmount: number;
  finalAmount: number;
  settlementAmount: number;
  isCouponApplied: boolean;
  appliedCoupon?: {
    couponId: number;
    couponCode: string;
    couponLabel: string;
    eligibleSubtotal: number;
  };
};

export const buildPricingSummary = (
  service: ServiceInfo | undefined,
  serviceInputs: ServiceFormServiceInputs | undefined,
  payment: FormPayment | undefined,
): PricingSummary => {
  const baseAmount = calculateBaseBillingAmount(service, serviceInputs);
  const currentContextKey = buildCouponContextKey(service, serviceInputs);
  const isCouponApplied = hasAppliedCouponForContext(payment, currentContextKey);
  const finalAmount = isCouponApplied
    ? payment?.billingAmount ?? baseAmount
    : baseAmount;
  const discountAmount = isCouponApplied
    ? payment?.couponDiscountAmount ?? Math.max(0, baseAmount - finalAmount)
    : 0;
  const settlementAmount =
    isCouponApplied && payment?.settlementAmount !== undefined
      ? payment.settlementAmount
      : calculateSettlementAmount(finalAmount, payment?.settlementMethod);

  return {
    baseAmount,
    discountAmount,
    finalAmount,
    settlementAmount,
    isCouponApplied,
    appliedCoupon:
      isCouponApplied && payment?.couponCode && payment?.couponId
        ? {
            couponId: payment.couponId,
            couponCode: payment.couponCode,
            couponLabel: payment.couponLabel ?? "",
            eligibleSubtotal: payment.couponEligibleSubtotal ?? baseAmount,
          }
        : undefined,
  };
};
